import { DQ9Rand } from '@/DQ9Rand'
import { Area } from '@/tmapBasicInfo/floorMapInfo/Area'
import { Divider } from '@/tmapBasicInfo/floorMapInfo/Divider'
import { Room } from '@/tmapBasicInfo/floorMapInfo/Room'
import { FloorMapData } from '@/tmapBasicInfo/floorMapInfo/FloorMapData'
import { Coordinate } from '@/interface/Coodinate'

/**
 * ドラクエ9 宝の地図の1フロア分のフロアマップデータを算出するクラス
 * @class FloorMapLogic
 */
class FloorMapLogic {
  /**
   * @type {DQ9Rand} ドラクエ9基本乱数インスタンス
   */
  private static _dq9rand: DQ9Rand = new DQ9Rand()

  /**
   * 地図ランク・地図シード・当該フロアの階数からフロアマップを算出する
   * @param {number} mapRank 地図ランク
   * @param {number} mapSeed 地図シード
   * @param {number} floor 当該フロアの階数
   * @returns {FloorMapData} 当該フロアのフロアマップデータ
   */
  public static calculateFloorMap(mapRank: number, mapSeed: number, floor: number): FloorMapData {
    const floorMapData: FloorMapData = new FloorMapData()
    // フロアマップデータ初期化
    floorMapData.mapRank = mapRank
    floorMapData.mapSeed = mapSeed
    floorMapData.floor = floor
    floorMapData.width = this.getFloorSize(mapSeed, floor)
    floorMapData.height = this.getFloorSize(mapSeed, floor)
    // シード初期化
    this._dq9rand.seed = mapSeed + floor
    // 全ての部分を壁で埋める
    this.fillWithWall(floorMapData)
    floorMapData.isCalculatedAsWhole = false
    // 領域オブジェクトを追加
    const area: Area = new Area()
    area.setCoordinate(1, 1, floorMapData.width - 2, floorMapData.height - 2)
    const list: Area[] = floorMapData.areas
    list.push(area)
    floorMapData.areas = list
    // 領域を分割
    this.divideArea(floorMapData, 0)
    // 分割した領域に部屋を作成
    for (let i = 0; i < floorMapData.areas.length; i++) {
      this.createRoom(floorMapData, i)
    }
    // 部屋を変形させる
    for (let i = 0; i < floorMapData.areas.length; i++) {
      this.transformRooms(floorMapData, i)
    }
    // 道を作る
    floorMapData.dividers.forEach((s) => {
      this.createPaths(floorMapData, s)
    })
    // 領域の上端と下端を壁で埋める
    for (let i = 0; i < floorMapData.width; i++) {
      floorMapData.setFloorMapValue(i, 0, 1)
      floorMapData.setFloorMapValue(i, floorMapData.height - 1, 1)
    }
    // 領域の左端と右端を壁で埋める
    for (let i = 0; i < floorMapData.height; i++) {
      floorMapData.setFloorMapValue(0, i, 1)
      floorMapData.setFloorMapValue(floorMapData.width - 1, i, 1)
    }
    // 上り階段・下り階段の設置
    this.putStairs(floorMapData)
    // 宝箱の設置
    if (floorMapData.floor >= 3) {
      this.putTreasureBoxes(floorMapData)
    }
    return floorMapData
  }

  /**
   * 最小値から最大値までの範囲から乱数で決定した値を返す
   * @param {number} min 最小値
   * @param {number} max 最大値
   * @returns {number} 決定後の値
   */
  private static getNumFromRange(min: number, max: number): number {
    if (min === max) {
      return min
    }
    const rand: number = this._dq9rand.generateRandom()
    const candidates: number = max - min + 1
    if (candidates == 0) {
      return min
    }
    return min + (rand % candidates)
  }

  /**
   * 当該フロアの1辺のサイズを求める
   * @param {number} mapSeed 地図シード
   * @param {number} floor 当該フロアの階数
   * @returns {number} 当該フロアの1辺のサイズ
   */
  private static getFloorSize(mapSeed: number, floor: number): number {
    return floor <= 4
      ? ((mapSeed + floor) % 5) + 10
      : floor <= 8
      ? ((mapSeed + floor) % 4) + 12
      : floor <= 12
      ? ((mapSeed + floor) % 3) + 14
      : 16
  }

  /**
   * 全てを壁で埋める
   * @param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   */
  private static fillWithWall(floorMapData: FloorMapData): void {
    for (let y = 0; y < 16; y++) {
      for (let x = 0; x < 16; x++) {
        floorMapData.setFloorMapValue(x, y, 1)
      }
    }
  }

  /**
   * 領域の分割を再帰的に行う
   * @param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   * @param {number} areaIndex 領域の要素番号
   */
  private static divideAreaRecursive(floorMapData: FloorMapData, areaIndex: number): void {
    const index: number = floorMapData.areas.length - 1
    if (this._dq9rand.generateRandom() % 2 !== 0) {
      this.divideArea(floorMapData, areaIndex)
      this.divideArea(floorMapData, index)
    } else {
      this.divideArea(floorMapData, index)
      this.divideArea(floorMapData, areaIndex)
    }
  }

  /**
   * 領域を上下または左右に分割する
   * @param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   * @param {number} areaIndex 領域の要素番号
   */
  private static divideArea(floorMapData: FloorMapData, areaIndex: number): void {
    if (floorMapData.areas.length >= 15) {
      return
    }
    // 左右に分割済みだった場合
    if (floorMapData.areas[areaIndex].isDividedLeftAndRight) {
      if (this.divideTopAndBottom(floorMapData, areaIndex)) {
        this.divideAreaRecursive(floorMapData, areaIndex)
      }
    }
    // 上下に分割済みだった場合
    else if (floorMapData.areas[areaIndex].isDividedTopAndBottom) {
      if (this.divideLeftAndRight(floorMapData, areaIndex)) {
        this.divideAreaRecursive(floorMapData, areaIndex)
      }
    }
    // どちらも分割済みではない場合は乱数によって方向を決める
    else if (this._dq9rand.generateRandom() % 2 !== 0) {
      if (this.divideLeftAndRight(floorMapData, areaIndex)) {
        this.divideAreaRecursive(floorMapData, areaIndex)
      }
    } else if (this.divideTopAndBottom(floorMapData, areaIndex)) {
      this.divideAreaRecursive(floorMapData, areaIndex)
    }
  }

  /**
   * 領域を上下に分割する
   * @param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   * @param {number} areaIndex 領域の要素番号
   * @returns {boolean} 分割に成功したかどうか
   */
  private static divideTopAndBottom(floorMapData: FloorMapData, areaIndex: number): boolean {
    const areas: Area[] = floorMapData.areas
    const height: number = areas[areaIndex].bottom + 1 - areas[areaIndex].top
    // 高さが7未満の場合は分割しない
    if (height < 7) {
      return false
    }
    // 既に上下に分割済みの場合は分割しない
    if (areas[areaIndex].isDividedTopAndBottom) {
      return false
    }
    const rand: number = this.getNumFromRange(0, height - 7)
    const position: number = areas[areaIndex].top + rand + 3
    for (let i = areas[areaIndex].left; i <= areas[areaIndex].right; i++) {
      floorMapData.setFloorMapValue(i, position, 3)
    }
    // 今回の分割で発生した新たな領域の座標を格納
    const newArea: Area = new Area()
    newArea.setCoordinate(
      areas[areaIndex].left,
      position + 1,
      areas[areaIndex].right,
      areas[areaIndex].bottom
    )
    areas.push(newArea)
    // 今回の分割によるy座標の更新
    areas[areaIndex].bottom = position - 1
    // 上下に分割したためフラグを更新
    areas[areaIndex].isDividedTopAndBottom = true
    floorMapData.areas = areas
    // 今回の分割で発生した仕切り棒の座標を格納
    const divider: Divider = new Divider()
    divider.setCoordinate(areas[areaIndex].left, position, areas[areaIndex].right, position)
    divider.areaIndex1 = areaIndex
    divider.areaIndex2 = floorMapData.areas.length - 1
    divider.direction = 1
    const dividers: Divider[] = floorMapData.dividers
    dividers.push(divider)
    floorMapData.dividers = dividers
    return true
  }

  /**
   * 領域を左右に分割する
   * @param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   * @param {number} areaIndex 領域の要素番号
   * @returns {boolean} 分割に成功したかどうか
   */
  private static divideLeftAndRight(floorMapData: FloorMapData, areaIndex: number): boolean {
    const areas: Area[] = floorMapData.areas
    const width: number = areas[areaIndex].right + 1 - areas[areaIndex].left
    // 幅が7未満の場合は分割しない
    if (width < 7) {
      return false
    }
    // 既に左右に分割済みの場合は分割しない
    if (areas[areaIndex].isDividedLeftAndRight) {
      return false
    }
    const rand: number = this.getNumFromRange(0, width - 7)
    const position: number = areas[areaIndex].left + rand + 3
    for (let i = areas[areaIndex].top; i <= areas[areaIndex].bottom; i++) {
      floorMapData.setFloorMapValue(position, i, 3)
    }
    // 今回の分割で発生した新たな領域の座標を格納
    const newArea: Area = new Area()
    newArea.setCoordinate(position + 1, areas[areaIndex].top, areas[areaIndex].right, areas[areaIndex].bottom)
    areas.push(newArea)
    // 今回の分割によるx座標の更新
    areas[areaIndex].right = position - 1
    // 左右に分割したためフラグを更新
    areas[areaIndex].isDividedLeftAndRight = true
    floorMapData.areas = areas
    // 今回の分割で発生した仕切り棒の座標を格納
    const divider: Divider = new Divider()
    divider.setCoordinate(position, areas[areaIndex].top, position, areas[areaIndex].bottom)
    divider.areaIndex1 = areaIndex
    divider.areaIndex2 = floorMapData.areas.length - 1
    divider.direction = 2
    const dividers: Divider[] = floorMapData.dividers
    dividers.push(divider)
    floorMapData.dividers = dividers
    return true
  }

  /**
   * 領域内に部屋を作る
   * @param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   * @param {number} areaIndex 領域の要素番号
   */
  private static createRoom(floorMapData: FloorMapData, areaIndex: number): void {
    const area: Area = floorMapData.areas[areaIndex]
    const width: number = area.right + 1 - area.left
    // 領域の横幅が3未満の場合は部屋を作成しない
    if (width < 3) {
      return
    }
    const height: number = area.bottom + 1 - area.top
    // 領域の高さが3未満の場合は部屋を作成しない
    if (height < 3) {
      return
    }
    let roomLeft: number, roomTop: number, roomRight: number, roomBottom: number
    if (this.getNumFromRange(0, 1) !== 0) {
      roomLeft = this.getNumFromRange(area.left, (area.left + (area.right - area.left + 1) / 3) | 0)
      roomTop = this.getNumFromRange(area.top, (area.top + (area.bottom - area.top + 1) / 3) | 0)
    } else {
      roomLeft = this.getNumFromRange(area.left + 1, (area.left + (area.right - area.left + 1) / 3) | 0)
      roomTop = this.getNumFromRange(area.top + 1, (area.top + (area.bottom - area.top + 1) / 3) | 0)
    }
    if (this.getNumFromRange(0, 1) !== 0) {
      roomRight = this.getNumFromRange(area.left + (((area.right - area.left + 1) / 3) | 0) * 2, area.right)
      roomBottom = this.getNumFromRange(area.top + (((area.bottom - area.top + 1) / 3) | 0) * 2, area.bottom)
    } else {
      roomRight = this.getNumFromRange(
        area.left + (((area.right - area.left + 1) / 3) | 0) * 2,
        area.right - 1
      )
      roomBottom = this.getNumFromRange(
        area.top + (((area.bottom - area.top + 1) / 3) | 0) * 2,
        area.bottom - 1
      )
    }
    // 部屋の座標を格納
    const room: Room = new Room()
    const rooms: Room[] = floorMapData.rooms
    room.setCoordinate(roomLeft, roomTop, roomRight, roomBottom)
    rooms.push(room)
    floorMapData.rooms = rooms
    // 部屋の座標が格納されているアドレスを格納
    area.roomIndex = floorMapData.rooms.length - 1
    //部屋の作成
    for (let i = roomTop; i <= roomBottom; i++) {
      for (let j = roomLeft; j <= roomRight; j++) {
        floorMapData.setFloorMapValue(j, i, 0)
      }
    }
    this.putNodes(floorMapData, area.roomIndex)
  }

  /**
   * 部屋にノードを配置する
   * @param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   * @param {number} roomIndex 部屋の要素番号
   */
  private static putNodes(floorMapData: FloorMapData, roomIndex: number): void {
    const room: Room = floorMapData.rooms[roomIndex]
    if (room.left == 0 || room.top == 0 || room.right == 0 || room.bottom == 0) {
      return
    }
    // 部屋の横幅が5未満の場合、上端・下端にそれぞれ1箇所ずつ「8」を代入
    if (room.right - room.left + 1 < 5) {
      room.topmostLeft = { x: this.getNumFromRange(room.left, room.right), y: room.top }
      room.bottommostLeft = { x: this.getNumFromRange(room.left, room.right), y: room.bottom }
      floorMapData.setFloorMapValue(room.topmostLeft.x, room.topmostLeft.y, 8)
      floorMapData.setFloorMapValue(room.bottommostLeft.x, room.bottommostLeft.y, 8)
    }
    // 部屋の横幅が5以上の場合、上端・下端にそれぞれ2箇所ずつ「8」を代入
    else {
      const half: number = room.left + (((room.right - room.left + 1) / 2) | 0) - 1
      room.topmostLeft = { x: this.getNumFromRange(room.left, half), y: room.top }
      room.topmostRight = { x: this.getNumFromRange(half + 1, room.right), y: room.top }
      room.bottommostLeft = { x: this.getNumFromRange(room.left, half), y: room.bottom }
      room.bottommostRight = { x: this.getNumFromRange(half + 1, room.right), y: room.bottom }
      floorMapData.setFloorMapValue(room.topmostLeft.x, room.topmostLeft.y, 8)
      floorMapData.setFloorMapValue(room.topmostRight.x, room.topmostRight.y, 8)
      floorMapData.setFloorMapValue(room.bottommostLeft.x, room.bottommostLeft.y, 8)
      floorMapData.setFloorMapValue(room.bottommostRight.x, room.bottommostRight.y, 8)
    }
    // 部屋の縦幅が5未満の場合、左端・右端にそれぞれ1箇所ずつ「8」を代入
    if (room.bottom - room.top + 1 < 5) {
      room.leftmostTop = { x: room.left, y: this.getNumFromRange(room.top, room.bottom) }
      room.rightmostTop = { x: room.right, y: this.getNumFromRange(room.top, room.bottom) }
      floorMapData.setFloorMapValue(room.leftmostTop.x, room.leftmostTop.y, 8)
      floorMapData.setFloorMapValue(room.rightmostTop.x, room.rightmostTop.y, 8)
    }
    // 部屋の縦幅が5以上の場合、左端・右端にそれぞれ2箇所ずつ「8」を代入
    else {
      const half: number = room.top + (((room.bottom - room.top + 1) / 2) | 0) - 1
      room.leftmostTop = { x: room.left, y: this.getNumFromRange(room.top, half) }
      room.leftmostBottom = { x: room.left, y: this.getNumFromRange(half + 1, room.bottom) }
      room.rightmostTop = { x: room.right, y: this.getNumFromRange(room.top, half) }
      room.rightmostBottom = { x: room.right, y: this.getNumFromRange(half + 1, room.bottom) }
      floorMapData.setFloorMapValue(room.leftmostTop.x, room.leftmostTop.y, 8)
      floorMapData.setFloorMapValue(room.leftmostBottom.x, room.leftmostBottom.y, 8)
      floorMapData.setFloorMapValue(room.rightmostTop.x, room.rightmostTop.y, 8)
      floorMapData.setFloorMapValue(room.rightmostBottom.x, room.rightmostBottom.y, 8)
    }
  }

  /**
   * 部屋をランダムに変形する
   * @param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   * @param {number} areaIndex 領域の要素番号
   */
  private static transformRooms(floorMapData: FloorMapData, areaIndex: number) {
    const area = floorMapData.areas[areaIndex]
    const room: Room = floorMapData.rooms[area.roomIndex]
    // 部屋の横幅の半分
    const halfWidth: number = ((room.right - room.left + 1) / 2) | 0
    // 部屋の縦幅の半分
    const halfHeight: number = ((room.bottom - room.top + 1) / 2) | 0
    // フロア全体から計算するか否かのフラグ
    let wholeFlag: boolean = false
    if (!floorMapData.isCalculatedAsWhole) {
      if (this.getNumFromRange(0, 15) == 0) {
        wholeFlag = true
      }
    }
    let leftDiff: number, topDiff: number, rightDiff: number, bottomDiff: number
    if (!wholeFlag) {
      leftDiff = room.left - area.left
      topDiff = room.top - area.top
      rightDiff = area.right - room.right
      bottomDiff = area.bottom - room.bottom
    } else {
      leftDiff = room.left - 1
      topDiff = room.top - 1
      rightDiff = floorMapData.width - room.right - 1
      bottomDiff = floorMapData.height - room.bottom - 1
      floorMapData.isCalculatedAsWhole = true
    }
    let mapValue: number, mapValue2: number, loopCount: number, loopCount2: number

    /*
     * 上方向
     */
    for (let i = room.left; i <= room.right; i++) {
      // 基準点の値
      mapValue = floorMapData.getFloorMapValue(i, room.top)
      if (mapValue == 1 || mapValue == 3) {
        continue
      }
      if (this.getNumFromRange(0, 1) != 0) {
        loopCount = this.getNumFromRange(0, topDiff)
        // 上に空間作成
        for (let j = 0; j < loopCount; j++) {
          if (floorMapData.getFloorMapValue(i, room.top - 1 - j) != 8) {
            floorMapData.setFloorMapValue(i, room.top - 1 - j, 0)
          }
        }
      } else {
        // 基準点の値が8だった場合は処理をスキップ
        if (mapValue == 8) {
          continue
        }
        // 基準点の1つ上の座標
        mapValue2 = floorMapData.getFloorMapValue(i, room.top - 1)
        if (mapValue2 != 1 && mapValue2 != 8) {
          loopCount2 = this.getNumFromRange(0, halfHeight)
          for (
            let j = 0;
            j < loopCount2 &&
            // 基準点が8ではない
            floorMapData.getFloorMapValue(i, room.top + j) != 8 &&
            // 基準点の1つ下が1ではない
            floorMapData.getFloorMapValue(i, room.top + j + 1) != 1 &&
            // 基準点の1つ右が1である
            (floorMapData.getFloorMapValue(i + 1, room.top + j) == 1 ||
              // 基準点の1つ右下が1ではない
              floorMapData.getFloorMapValue(i + 1, room.top + j + 1) != 1) &&
            // 基準点の1つ左が1である
            (floorMapData.getFloorMapValue(i - 1, room.top + j) == 1 ||
              // 基準点の1つ左下が1ではない
              floorMapData.getFloorMapValue(i - 1, room.top + j + 1) != 1);
            j++
          ) {
            // 基準点を壁にする
            floorMapData.setFloorMapValue(i, room.top + j, 1)
          }
        }
      }
    }

    /*
     * 下方向
     */
    for (let i = room.left; i <= room.right; i++) {
      // 基準点の値
      mapValue = floorMapData.getFloorMapValue(i, room.bottom)
      if (mapValue == 1 || mapValue == 3) {
        continue
      }
      if (this.getNumFromRange(0, 1) != 0) {
        loopCount = this.getNumFromRange(0, bottomDiff)
        // 下に空間作成
        for (let j = 0; j < loopCount; j++) {
          if (floorMapData.getFloorMapValue(i, room.bottom + 1 + j) != 8) {
            floorMapData.setFloorMapValue(i, room.bottom + 1 + j, 0)
          }
        }
      } else {
        // 基準点の値が8だった場合は処理をスキップ
        if (mapValue == 8) {
          continue
        }
        // 基準点の1つ下の座標
        mapValue2 = floorMapData.getFloorMapValue(i, room.bottom + 1)
        if (mapValue2 != 1 && mapValue2 != 8) {
          loopCount2 = this.getNumFromRange(0, halfHeight)
          for (
            let j = 0;
            j < loopCount2 &&
            // 基準点が8ではない
            floorMapData.getFloorMapValue(i, room.bottom - j) != 8 &&
            // 基準点の1つ上が1ではない
            floorMapData.getFloorMapValue(i, room.bottom - j - 1) != 1 &&
            // 基準点の1つ右が1である
            (floorMapData.getFloorMapValue(i + 1, room.bottom - j) == 1 ||
              // 基準点の1つ右上が1ではない
              floorMapData.getFloorMapValue(i + 1, room.bottom - j - 1) != 1) &&
            // 基準点の1つ左が1である
            (floorMapData.getFloorMapValue(i - 1, room.bottom - j) == 1 ||
              // 基準点の1つ左上が1ではない
              floorMapData.getFloorMapValue(i - 1, room.bottom - j - 1) != 1);
            j++
          ) {
            // 基準点を壁にする
            floorMapData.setFloorMapValue(i, room.bottom - j, 1)
          }
        }
      }
    }

    /*
     * 左方向
     */
    for (let i = room.top; i <= room.bottom; i++) {
      // 基準点の値
      mapValue = floorMapData.getFloorMapValue(room.left, i)
      if (mapValue == 1 || mapValue == 3) {
        continue
      }
      if (this.getNumFromRange(0, 1) != 0) {
        loopCount = this.getNumFromRange(0, leftDiff)
        // 左に空間作成
        for (let j = 0; j < loopCount; j++) {
          if (floorMapData.getFloorMapValue(room.left - j - 1, i) != 8) {
            floorMapData.setFloorMapValue(room.left - j - 1, i, 0)
          }
        }
      } else {
        // 基準点の値が8だった場合は処理をスキップ
        if (mapValue == 8) {
          continue
        }
        // 基準点の1つ左の座標
        mapValue2 = floorMapData.getFloorMapValue(room.left - 1, i)
        if (mapValue2 != 1 && mapValue2 != 8) {
          loopCount2 = this.getNumFromRange(0, halfWidth)
          for (
            let j = 0;
            j < loopCount2 &&
            // 基準点が8ではない
            floorMapData.getFloorMapValue(room.left + j, i) != 8 &&
            // 基準点の1つ右が1ではない
            floorMapData.getFloorMapValue(room.left + j + 1, i) != 1 &&
            // 基準点の1つ下が1である
            (floorMapData.getFloorMapValue(room.left + j, i + 1) == 1 ||
              // 基準点の1つ右下が1ではない
              floorMapData.getFloorMapValue(room.left + j + 1, i + 1) != 1) &&
            // 基準点の1つ上が1である
            (floorMapData.getFloorMapValue(room.left + j, i - 1) == 1 ||
              // 基準点の1つ右上が1ではない
              floorMapData.getFloorMapValue(room.left + j + 1, i - 1) != 1);
            j++
          ) {
            // 基準点を壁にする
            floorMapData.setFloorMapValue(room.left + j, i, 1)
          }
        }
      }
    }

    /*
     * 右方向
     */
    for (let i = room.top; i <= room.bottom; i++) {
      // 基準点の値
      mapValue = floorMapData.getFloorMapValue(room.right, i)
      if (mapValue == 1 || mapValue == 3) {
        continue
      }
      if (this.getNumFromRange(0, 1) != 0) {
        loopCount = this.getNumFromRange(0, rightDiff)
        // 右に空間作成
        for (let j = 0; j < loopCount; j++) {
          if (floorMapData.getFloorMapValue(room.right + j + 1, i) != 8) {
            floorMapData.setFloorMapValue(room.right + j + 1, i, 0)
          }
        }
      } else {
        // 基準点の値が8だった場合は処理をスキップ
        if (mapValue == 8) {
          continue
        }
        // 基準点の1つ右の座標（ここだけ何故かroom.topを参照している？）
        mapValue2 = floorMapData.getFloorMapValue(room.top + 1, i)
        if (mapValue2 != 1 && mapValue2 != 8) {
          loopCount2 = this.getNumFromRange(0, halfWidth)
          for (
            let j = 0;
            j < loopCount2 &&
            // 基準点が8ではない
            floorMapData.getFloorMapValue(room.right - j, i) != 8 &&
            // 基準点の1つ左が1ではない
            floorMapData.getFloorMapValue(room.right - j - 1, i) != 1 &&
            // 基準点の1つ下が1である
            (floorMapData.getFloorMapValue(room.right - j, i + 1) == 1 ||
              // 基準点の1つ左下が1ではない
              floorMapData.getFloorMapValue(room.right - j - 1, i + 1) != 1) &&
            // 基準点の1つ上が1である
            (floorMapData.getFloorMapValue(room.right - j, i - 1) == 1 ||
              // 基準点の1つ左上が1ではない
              floorMapData.getFloorMapValue(room.right - j - 1, i - 1) != 1);
            j++
          ) {
            // 基準点を壁にする
            floorMapData.setFloorMapValue(room.right - j, i, 1)
          }
        }
      }
    }
  }

  /**
   * 各部屋同士をつなぐ道を作る
   * @param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   * @param {Divider} divider 仕切り棒のインスタンス
   */
  private static createPaths(floorMapData: FloorMapData, divider: Divider): void {
    let room1: Room, room2: Room
    let allChangeFlag: number
    // 仕切り棒が横のとき
    if (divider.direction == 1) {
      room1 = floorMapData.rooms[floorMapData.areas[divider.areaIndex1].roomIndex]
      room2 = floorMapData.rooms[floorMapData.areas[divider.areaIndex2].roomIndex]
      allChangeFlag = this.getNumFromRange(0, 7) == 0 ? 1 : 0
      this.connectTopAndBottom(floorMapData, room1.bottommostLeft, room2.topmostLeft, divider, allChangeFlag)
      allChangeFlag = this.getNumFromRange(0, 7) == 0 ? 1 : 0
      this.connectTopAndBottom(floorMapData, room1.bottommostRight, room2.topmostLeft, divider, allChangeFlag)
      allChangeFlag = this.getNumFromRange(0, 7) == 0 ? 1 : 0
      this.connectTopAndBottom(floorMapData, room1.bottommostLeft, room2.topmostRight, divider, allChangeFlag)
      allChangeFlag = this.getNumFromRange(0, 7) == 0 ? 1 : 0
      this.connectTopAndBottom(
        floorMapData,
        room1.bottommostRight,
        room2.topmostRight,
        divider,
        allChangeFlag
      )
    }
    // 仕切り棒が縦のとき
    else if (divider.direction == 2) {
      room1 = floorMapData.rooms[floorMapData.areas[divider.areaIndex1].roomIndex]
      room2 = floorMapData.rooms[floorMapData.areas[divider.areaIndex2].roomIndex]
      allChangeFlag = this.getNumFromRange(0, 7) == 0 ? 1 : 0
      this.connectLeftAndRight(floorMapData, room1.rightmostTop, room2.leftmostTop, divider, allChangeFlag)
      allChangeFlag = this.getNumFromRange(0, 7) == 0 ? 1 : 0
      this.connectLeftAndRight(floorMapData, room1.rightmostBottom, room2.leftmostTop, divider, allChangeFlag)
      allChangeFlag = this.getNumFromRange(0, 7) == 0 ? 1 : 0
      this.connectLeftAndRight(floorMapData, room1.rightmostTop, room2.leftmostBottom, divider, allChangeFlag)
      allChangeFlag = this.getNumFromRange(0, 7) == 0 ? 1 : 0
      this.connectLeftAndRight(
        floorMapData,
        room1.rightmostBottom,
        room2.leftmostBottom,
        divider,
        allChangeFlag
      )
    }
  }

  /**
   * 上下に分かれた部屋を接続する
   *@param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   * @param {Coordinate} node1 ノード1
   * @param {Coordinate} node2 ノード2
   * @param {Divider} divider 仕切り棒のインスタンス
   * @param {number} allChangeFlag 仕切り棒が存在する行を全て道にするかどうか
   */
  private static connectTopAndBottom(
    floorMapData: FloorMapData,
    node1: Coordinate,
    node2: Coordinate,
    divider: Divider,
    allChangeFlag: number
  ): void {
    if (node1.x == 0 || node1.y == 0 || node2.x == 0 || node2.y == 0) {
      return
    }
    // 部屋room1から仕切り棒までを（上から下に向かって）道にする
    for (let i = node1.y + 1; i <= divider.startY; i++) {
      floorMapData.setFloorMapValue(node1.x, i, 2)
    }
    // 部屋room2から仕切りまでを（下から上に向かって）道にする
    for (let i = node2.y - 1; i > divider.startY; i--) {
      floorMapData.setFloorMapValue(node2.x, i, 2)
    }
    // room1から生成した道とroom2から生成した道のx座標がズレているときは仕切り棒でそれらを接続する
    if (node1.x < node2.x) {
      for (let i = node1.x; i <= node2.x; i++) {
        floorMapData.setFloorMapValue(i, divider.startY, 2)
      }
    } else if (node1.x > node2.x) {
      for (let i = node2.x; i <= node1.x; i++) {
        floorMapData.setFloorMapValue(i, divider.startY, 2)
      }
    }
    // 8分の7の確率で処理終了
    if (allChangeFlag == 0) {
      return
    }
    // 仕切り棒が存在する行を道にする
    if (node1.x < node2.x) {
      for (let i = node2.x + 1; i < 16; i++) {
        if (
          floorMapData.getFloorMapValue(i, divider.startY) != 1 &&
          floorMapData.getFloorMapValue(i, divider.startY) != 3
        ) {
          for (let j = node2.x + 1; j < i; j++) {
            floorMapData.setFloorMapValue(j, divider.startY, 2)
          }
          break
        }
      }
      for (let i = node1.x - 1; i >= 0; i--) {
        if (
          floorMapData.getFloorMapValue(i, divider.startY) != 1 &&
          floorMapData.getFloorMapValue(i, divider.startY) != 3
        ) {
          for (let j = node1.x - 1; j > i; j--) {
            floorMapData.setFloorMapValue(j, divider.startY, 2)
          }
          break
        }
      }
    } else {
      for (let i = node1.x + 1; i < 16; i++) {
        if (
          floorMapData.getFloorMapValue(i, divider.startY) != 1 &&
          floorMapData.getFloorMapValue(i, divider.startY) != 3
        ) {
          for (let j = node1.x + 1; j < i; j++) {
            floorMapData.setFloorMapValue(j, divider.startY, 2)
          }
          break
        }
      }
      for (let i = node2.x - 1; i >= 0; i--) {
        if (
          floorMapData.getFloorMapValue(i, divider.startY) != 1 &&
          floorMapData.getFloorMapValue(i, divider.startY) != 3
        ) {
          for (let j = node2.x - 1; j > i; j--) {
            floorMapData.setFloorMapValue(j, divider.startY, 2)
          }
          break
        }
      }
    }
  }

  /**
   * 左右に分かれた部屋を接続する
   * @param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   * @param {Coordinate} node1 ノード1
   * @param {Coordinate} node2 ノード2
   * @param {Divider} divider 仕切り棒のインスタンス
   * @param {number} allChangeFlag 仕切り棒が存在する列を全て道にするかどうか
   */
  private static connectLeftAndRight(
    floorMapData: FloorMapData,
    node1: Coordinate,
    node2: Coordinate,
    divider: Divider,
    allChangeFlag: number
  ): void {
    if (node1.x == 0 || node1.y == 0 || node2.x == 0 || node2.y == 0) {
      return
    }
    // 部屋room1から仕切り棒までを（左から右に向かって）道にする
    for (let i = node1.x + 1; i <= divider.startX; i++) {
      floorMapData.setFloorMapValue(i, node1.y, 2)
    }
    // 部屋room2から仕切り棒までを（右から左に向かって）道にする
    for (let i = node2.x - 1; i > divider.startX; i--) {
      floorMapData.setFloorMapValue(i, node2.y, 2)
    }
    // room1から生成した道とroom2から生成した道のy座標がズレているときは仕切り棒でそれらを接続する
    if (node1.y < node2.y) {
      for (let i = node1.y; i <= node2.y; i++) {
        floorMapData.setFloorMapValue(divider.startX, i, 2)
      }
    } else if (node1.y > node2.y) {
      for (let i = node2.y; i <= node1.y; i++) {
        floorMapData.setFloorMapValue(divider.startX, i, 2)
      }
    }
    // 8分の7の確率で処理終了
    if (allChangeFlag == 0) {
      return
    }
    // 仕切り棒が存在する列を道にする
    if (node1.y < node2.y) {
      for (let i = node2.y + 1; i < 16; i++) {
        if (
          floorMapData.getFloorMapValue(divider.startX, i) != 1 &&
          floorMapData.getFloorMapValue(divider.startX, i) != 3
        ) {
          for (let j = node2.y + 1; j < i; j++) {
            floorMapData.setFloorMapValue(divider.startX, j, 2)
          }
          break
        }
      }
      for (let i = node1.y - 1; i >= 0; i--) {
        if (
          floorMapData.getFloorMapValue(divider.startX, i) != 1 &&
          floorMapData.getFloorMapValue(divider.startX, i) != 3
        ) {
          for (let j = node1.y - 1; j > i; j--) {
            floorMapData.setFloorMapValue(divider.startX, j, 2)
          }
          break
        }
      }
    } else {
      for (let i = node1.y + 1; i < 16; i++) {
        if (
          floorMapData.getFloorMapValue(divider.startX, i) != 1 &&
          floorMapData.getFloorMapValue(divider.startX, i) != 3
        ) {
          for (let j = node1.y + 1; j < i; j++) {
            floorMapData.setFloorMapValue(divider.startX, j, 2)
          }
          break
        }
      }
      for (let i = node2.y - 1; i >= 0; i--) {
        if (
          floorMapData.getFloorMapValue(divider.startX, i) != 1 &&
          floorMapData.getFloorMapValue(divider.startX, i) != 3
        ) {
          for (let j = node2.y - 1; j > i; j--) {
            floorMapData.setFloorMapValue(divider.startX, j, 2)
          }
          break
        }
      }
    }
  }

  /**
   * 上り階段・下り階段を設置する
   * @param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   */
  private static putStairs(floorMapData: FloorMapData): void {
    let loopCount: number = 0
    let loopCount2: number = 0
    let upStairsRoomIndex: number = 0
    let upStairsX: number = 0
    let upStairsY: number = 0
    let downStairsRoomIndex: number = 0
    let downStairsX: number = 0
    let downStairsY: number = 0
    let left: number, top: number, right: number, bottom: number
    const list: number[] = [234, 171, 174, 186, 163, 142, 58, 232, 184, 226, 139, 46]
    for (;;) {
      upStairsRoomIndex = this.getNumFromRange(0, floorMapData.rooms.length - 1)
      upStairsX = this.getNumFromRange(
        floorMapData.rooms[upStairsRoomIndex].left,
        floorMapData.rooms[upStairsRoomIndex].right
      )
      upStairsY = this.getNumFromRange(
        floorMapData.rooms[upStairsRoomIndex].top,
        floorMapData.rooms[upStairsRoomIndex].bottom
      )
      if (loopCount < 100) {
        left = floorMapData.getFloorMapValue(upStairsX - 1, upStairsY)
        top = floorMapData.getFloorMapValue(upStairsX, upStairsY - 1)
        right = floorMapData.getFloorMapValue(upStairsX + 1, upStairsY)
        bottom = floorMapData.getFloorMapValue(upStairsX, upStairsY + 1)
        // 壁or仕切りかどうかを判定
        left = left == 1 || left == 3 ? 1 : 0
        top = top == 1 || top == 3 ? 1 : 0
        right = right == 1 || right == 3 ? 1 : 0
        bottom = bottom == 1 || bottom == 3 ? 1 : 0
        if (left * top != 0 || right * top != 0 || left * bottom != 0 || right * bottom != 0) {
          loopCount++
          continue
        }
        if (list.includes(this.convertToBinaryDigits(floorMapData, upStairsX, upStairsY))) {
          loopCount++
          continue
        }
      }
      floorMapData.setFloorMapValue(upStairsX, upStairsY, 4)
      floorMapData.upStairsCoordinate = { x: upStairsX, y: upStairsY }
      // ハマる地図の修正
      if (loopCount >= 100) {
        loopCount = 0
        loopCount2 = 0
      }
      for (;;) {
        downStairsRoomIndex = this.getNumFromRange(0, floorMapData.rooms.length - 1)
        if (downStairsRoomIndex == upStairsRoomIndex && loopCount2 < 25) {
          loopCount2++
          continue
        }
        downStairsX = this.getNumFromRange(
          floorMapData.rooms[downStairsRoomIndex].left,
          floorMapData.rooms[downStairsRoomIndex].right
        )
        downStairsY = this.getNumFromRange(
          floorMapData.rooms[downStairsRoomIndex].top,
          floorMapData.rooms[downStairsRoomIndex].bottom
        )
        if (
          downStairsRoomIndex == upStairsRoomIndex &&
          downStairsX == upStairsX &&
          downStairsY == upStairsY
        ) {
          continue
        }
        break
      }
      left = floorMapData.getFloorMapValue(downStairsX - 1, downStairsY)
      top = floorMapData.getFloorMapValue(downStairsX, downStairsY - 1)
      right = floorMapData.getFloorMapValue(downStairsX + 1, downStairsY)
      bottom = floorMapData.getFloorMapValue(downStairsX, downStairsY + 1)
      // 壁or仕切りかどうかを判定
      left = left == 1 || left == 3 ? 1 : 0
      top = top == 1 || top == 3 ? 1 : 0
      right = right == 1 || right == 3 ? 1 : 0
      bottom = bottom == 1 || bottom == 3 ? 1 : 0
      if (left * top != 0 || right * top != 0 || left * bottom != 0 || right * bottom != 0) {
        loopCount++
        continue
      }
      break
    }
    floorMapData.setFloorMapValue(downStairsX, downStairsY, 5)
    floorMapData.downStairsCoordinate = { x: downStairsX, y: downStairsY }
  }

  /**
   * 指定座標の周囲8マスを2進数8桁で表現する（壁ならば1、そうでなければ0）
   * @param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   * @param {number} x x座標
   * @param {number} y y座標
   * @returns 2進数8桁に変換した値
   */
  private static convertToBinaryDigits(floorMapData: FloorMapData, x: number, y: number): number {
    const middle: number = floorMapData.getFloorMapValue(x, y)
    if (middle == 1 || middle == 3) {
      return 255
    }
    const left: number = floorMapData.getFloorMapValue(x - 1, y)
    const bottomLeft: number = floorMapData.getFloorMapValue(x - 1, y + 1)
    const bottom: number = floorMapData.getFloorMapValue(x, y + 1)
    const bottomRight: number = floorMapData.getFloorMapValue(x + 1, y + 1)
    const right: number = floorMapData.getFloorMapValue(x + 1, y)
    const topRight: number = floorMapData.getFloorMapValue(x + 1, y - 1)
    const top: number = floorMapData.getFloorMapValue(x, y - 1)
    const topLeft: number = floorMapData.getFloorMapValue(x - 1, y - 1)

    let ret: number = topLeft == 1 || topLeft == 3 ? 128 : 0
    ret = topRight == 1 || topRight == 3 ? ret | 32 : ret & 0xdf
    ret = bottomRight == 1 || bottomRight == 3 ? ret | 8 : ret & 0xf7
    ret = bottomLeft == 1 || bottomLeft == 3 ? ret | 2 : ret & 0xfd
    // 上が壁または仕切りだった場合、右上と左上も壁とみなす
    ret = top == 1 || top == 3 ? ret | 224 : ret & 0xbf
    // 右が壁または仕切りだった場合、右上と右下も壁とみなす
    ret = right == 1 || right == 3 ? ret | 56 : ret & 0xef
    // 下が壁または仕切りだった場合、右下と左下も壁とみなす
    ret = bottom == 1 || bottom == 3 ? ret | 14 : ret & 0xfb
    // 左が壁または仕切りだった場合、左上と左下も壁とみなす
    ret = left == 1 || left == 3 ? ret | 131 : ret & 0xfe
    return ret
  }

  /**
   * 宝箱を設置する
   * @param {FloorMapData} floorMapData 当該フロアのフロアマップデータ
   */
  private static putTreasureBoxes(floorMapData: FloorMapData): void {
    const treasureBoxCount: number = this.getNumFromRange(1, 3)
    let putTreasureBoxCount: number = 0
    let retryCount: number = 0
    do {
      const roomIndex: number = this.getNumFromRange(0, floorMapData.rooms.length - 1)
      const x: number = this.getNumFromRange(
        floorMapData.rooms[roomIndex].left,
        floorMapData.rooms[roomIndex].right
      )
      const y: number = this.getNumFromRange(
        floorMapData.rooms[roomIndex].top,
        floorMapData.rooms[roomIndex].bottom
      )
      const mapValue: number = floorMapData.getFloorMapValue(x, y)
      if (retryCount < 100 && floorMapData.floor == 3) {
        retryCount++
        continue
      }
      if (mapValue == 4 || mapValue == 5 || mapValue >= 10) {
        retryCount++
        continue
      }
      floorMapData.setFloorMapValue(x, y, 10 * (putTreasureBoxCount + 1))
      const treasureBoxCoordinates: Coordinate[] = floorMapData.treasureBoxCoordinates
      treasureBoxCoordinates.push({ x, y })
      floorMapData.treasureBoxCoordinates = treasureBoxCoordinates
      putTreasureBoxCount++
    } while (treasureBoxCount > putTreasureBoxCount)
  }
}

export { FloorMapLogic }
